import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui/Button';
import { bindPwaUpdateControls } from '@/features/pwa/pwaUpdate';
import styles from './PwaUpdateBanner.module.css';

export function PwaUpdateBanner() {
  const { t } = useTranslation();
  const [showInstall, setShowInstall] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, reg) {
      registrationRef.current = reg ?? null;
      bindPwaUpdateControls({
        registration: reg,
        updateServiceWorker,
      });
    },
  });

  useEffect(() => {
    bindPwaUpdateControls({
      registration: registrationRef.current,
      updateServiceWorker,
    });
  }, [updateServiceWorker]);

  useEffect(() => {
    const tick = () => {
      void registrationRef.current?.update();
    };
    tick();
    const id = window.setInterval(tick, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!needRefresh && !showInstall) return null;

  return (
    <div className={styles.banner} role="status">
      {needRefresh ? (
        <>
          <span>{t('pwa.updateAvailable')}</span>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              void updateServiceWorker(true);
              setNeedRefresh(false);
            }}
          >
            {t('pwa.updateNow')}
          </Button>
        </>
      ) : (
        <>
          <span>{t('pwa.installHint')}</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void deferred?.prompt();
              setShowInstall(false);
            }}
          >
            {t('pwa.install')}
          </Button>
        </>
      )}
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}
